import { $, el, esc, limpiar, noContesto, pedir, puede, rpcLee, sb, TOPE_SERVIDOR } from './nucleo';

// ---------- la rentabilidad (punto 9, sesión B) ----------
//
//  Una pantalla de LECTURA sobre dos funciones de la base (0050):
//  `public.rentabilidad(desde, hasta, agrupar)` y
//  `public.rentabilidad_detalle(desde, hasta, filtros…)`. Las dos son
//  `security definer`, exigen `costo.ver` y tiran 42501 si falta; las dos son
//  `stable`, así que van por `rpcLee()` y no por `rpc()` (D99). La cuenta la
//  hace la base entera (qué es una venta, su fecha en el día de la empresa
//  —D39—, el prorrateo del descuento, el margen). Esta pantalla suma filas ya
//  hechas y dice qué les falta.
//
//  NO PUEDE AFIRMAR UN MARGEN QUE NO ESTÁ (D112). La base devuelve `costo`,
//  `comision`, `margen` y `margen_pct` en NULL —no en cero— cuando ningún ítem
//  del grupo tiene costo, y `sin_costo` cuenta cuántos quedaron afuera. Por eso:
//   · un total de márgenes suma SÓLO los que no son null, y si son todos null
//     el total es «sin costo», no 0,00;
//   · el % del total se calcula sobre `ventas_con_costo`, que es lo único
//     contra lo que el margen se puede comparar — sobre `ventas` saldría un
//     porcentaje más chico que nadie midió;
//   · y cuando algo quedó afuera hay un cartel que no se apaga y dice cuánto.

//  Las filas, con los null que la base DE VERDAD devuelve. Ojo: los tipos
//  generados dicen `costo: number`; leerlos así es el `coalesce(costo, 0)` que
//  la D112 prohíbe.
type Fila = {
  clave: string | null; etiqueta: string; unidades: number;
  ventas: number; ventas_con_costo: number;
  costo: number | null; comision: number | null;
  margen: number | null; margen_pct: number | null; sin_costo: number;
};
type Item = {
  item_id: number; dia: string; pedido: string; cliente: string | null;
  vendedor: string | null; producto: string; descripcion: string;
  categoria: string | null; serie: string | null; proveedor: string | null;
  cantidad: number; neto: number; costo: number | null; comision: number | null;
  margen: number | null; tope: boolean;
};

//  Los once agrupadores de la función, con su nombre y —para los que agrupan
//  por una FILA de otra tabla— el parámetro de `rentabilidad_detalle` que
//  filtra por ella. Los de fecha no llevan filtro: el detalle se pide con el
//  desde/hasta de ese día, semana o mes. Los tres de la 0090 (D206) se abren
//  con `rentabilidad_grupo`, que filtra por la misma clave de la fila
//  (`porClave`).
const AGRUPA: Record<string, { nombre: string; filtro?: string; porClave?: true }> = {
  categoria: { nombre: 'Categoría', filtro: 'p_categoria_id' },
  modelo:    { nombre: 'Modelo',    porClave: true },
  producto:  { nombre: 'Producto',  filtro: 'p_producto_id' },
  marca:     { nombre: 'Marca',     porClave: true },
  unidad:    { nombre: 'Unidad vendida', porClave: true },
  vendedor:  { nombre: 'Vendedor',  filtro: 'p_vendedor_id' },
  cliente:   { nombre: 'Cliente',   filtro: 'p_cliente_id' },
  proveedor: { nombre: 'Proveedor', filtro: 'p_proveedor_id' },
  dia:       { nombre: 'Día' },
  semana:    { nombre: 'Semana' },
  mes:       { nombre: 'Mes' },
};

//  Lo que dice cada agrupador cuando la fila no tiene de qué agruparse
//  (`clave` null → la base escribe «(sin dato)»). El común es el PROVEEDOR: sale
//  de la recepción de la UNIDAD, y lo no serializado no tiene.
const SIN_DATO: Record<string, string> = {
  proveedor: 'Sin proveedor conocido: lo que no se vende por número de serie no sabe de qué recepción salió',
  categoria: 'Productos sin categoría',
  marca:     'Productos sin marca',
  cliente:   'Sin cliente',
  vendedor:  'Sin vendedor',
};

//  EL TOPE DEL SERVIDOR, que no es el de la base. PostgREST corta toda
//  respuesta en «Max rows» (1.000 por omisión) sin avisar, así que el `tope`
//  de 2.000 del detalle nunca llega. Ojo: una respuesta de mil filas o más no
//  se afirma entera, en las dos lecturas.

// ---------- lo que elige quien mira (D206) ----------
//
//  CÓMO SE VE LA GANANCIA, en tres formas (lo pidió Steven al corregir la
//  maqueta del dueño, 17/10):
//   · `costo`:    venta − costo. Es `margen + comision`: la base resta la
//                 comisión de los mismos ítems que tienen costo, así que
//                 sumarla de vuelta da venta − costo, y se deduce sin otra
//                 columna. Con `margen` en null, también null (D112).
//   · `comision`: venta − costo − comisiones, el margen de siempre.
//   · `todo`:     además, los envíos que paga la empresa y los gastos de
//                 caja del período (`rentabilidad_gastos`). Sólo en el total:
//                 un gasto no es de ninguna fila (lo eligió Steven, 19/10).
//  Qué agrupa, qué forma y qué muestra el gráfico se recuerdan en ESTA
//  computadora (`localStorage`); sin almacenamiento, se usan igual y se
//  olvidan al recargar. Arranca por categoría (la maqueta).
type Vista = 'costo' | 'comision' | 'todo';
type Metrica = 'margen' | 'ventas' | 'pct' | 'unidades';
const CLAVE_PREF = 'nubilon.rentabilidad';
let VISTA: Vista = 'comision', METRICA: Metrica = 'margen', PREF_LEIDA = false;
const leerPref = () => {
  let p: any = null;
  try { p = JSON.parse(localStorage.getItem(CLAVE_PREF) ?? 'null'); } catch { /* sin almacenamiento */ }
  $('#rnAgrupar').value = p?.agrupar in AGRUPA ? p.agrupar : 'categoria';
  if (['costo','comision','todo'].includes(p?.vista)) VISTA = p.vista;
  if (['margen','ventas','pct','unidades'].includes(p?.metrica)) METRICA = p.metrica;
};
const guardarPref = () => {
  try {
    localStorage.setItem(CLAVE_PREF, JSON.stringify(
      { agrupar: $('#rnAgrupar').value, vista: VISTA, metrica: METRICA }));
  } catch { /* sin almacenamiento: vale hasta recargar */ }
};
const marcarBotones = () => {
  document.querySelectorAll<HTMLElement>('#rnVista button').forEach(b =>
    b.setAttribute('aria-pressed', String(b.dataset.v === VISTA)));
  document.querySelectorAll<HTMLElement>('#rnMetrica button').forEach(b =>
    b.setAttribute('aria-pressed', String(b.dataset.m === METRICA)));
  $('#rnVistaTxt').textContent = VISTA === 'costo'
    ? 'Ganancia = venta − costo, antes de pagar comisiones.'
    : VISTA === 'comision'
    ? 'Ganancia = venta − costo − la comisión del vendedor. El envío no entra.'
    : 'Ganancia = venta − costo − comisiones, y en el total se restan además los envíos que paga la empresa y los gastos de caja del período.';
};
const sinCostoChip = '<span class="chip c-warn">sin costo</span>';
//  La ganancia de una fila (o del total) según la forma elegida.
const ganancia = (margen: number | null, comision: number | null) =>
  margen == null ? null : VISTA === 'costo' ? Number(margen) + Number(comision ?? 0) : Number(margen);
const pctDe = (g: number | null, base: number) => g == null || !(base > 0) ? null : g / base * 100;

// ---------- la configuración que la pantalla necesita leer ----------
//
//  DOS valores de `config` que la pantalla LEE (D96):
//   · `empresa.zona_horaria`: «este mes» es el de la EMPRESA (D39); sin la
//     fila `app.dia_empresa()` usa UTC, y la pantalla también.
//   · `precio.moneda`: sin la fila es USD, igual que `app.config_texto`.
//  Si la LECTURA falla no se inventa nada: sin moneda al lado de los números y
//  fechas con el reloj de esta computadora, DICIÉNDOLO.
let ZONA: string | null = null, MONEDA: string | null = null, E_CFG = '';
let PEDIDO_CFG: Promise<void> | null = null;
const textoConfig = (v: unknown) => v == null ? null
  : (typeof v === 'string' ? v : JSON.stringify(v)).trim() || null;
const leerCfg = async () => {
  const { data, error } = await pedir(sb.from('config').select('clave,valor')
    .in('clave', ['empresa.zona_horaria', 'precio.moneda']));
  E_CFG = error ? error.message : '';
  if (error){ ZONA = null; MONEDA = null; return; }
  const v = (k: string) => (data || []).find((r: any) => r.clave === k)?.valor;
  ZONA   = textoConfig(v('empresa.zona_horaria')) ?? 'UTC';
  MONEDA = textoConfig(v('precio.moneda')) ?? 'USD';
};
//  Una sola lectura por sesión mientras ande; si falló, se reintenta.
const cfg = () => (PEDIDO_CFG ??= leerCfg()
  .then(() => { if (E_CFG) PEDIDO_CFG = null; }));

// ---------- el período ----------
//
//  Fechas `AAAA-MM-DD`. Ojo: la aritmética va en UTC a propósito; en la zona
//  del navegador un cambio de horario se come un día.
let ZONA_USADA: 'empresa' | 'local' = 'empresa';
const hoy = (): string => {
  if (ZONA){
    try {
      ZONA_USADA = 'empresa';
      return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric',
        month: '2-digit', day: '2-digit' }).format(new Date());
    } catch { /* una zona que el navegador no conoce: abajo, y se dice */ }
  }
  ZONA_USADA = 'local';
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const sumarDias = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const finDeMes = (iso: string) => {
  const d = new Date(iso.slice(0, 7) + '-01T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
};
const dma = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const mayor = (a: string, b: string) => a > b ? a : b;
const menor = (a: string, b: string) => a < b ? a : b;

type Periodo = { desde: string; hasta: string };
const periodo = (): Periodo | null => {
  const p = $('#rnPeriodo').value;
  if (p === 'rango'){
    const desde = $('#rnDesde').value, hasta = $('#rnHasta').value;
    return desde && hasta ? { desde, hasta } : null;
  }
  const h = hoy();
  if (p === 'mesAnt'){
    const fin = sumarDias(h.slice(0, 8) + '01', -1);
    return { desde: fin.slice(0, 8) + '01', hasta: fin };
  }
  if (p === '30d') return { desde: sumarDias(h, -29), hasta: h };
  return { desde: h.slice(0, 8) + '01', hasta: h };
};

// ---------- la lectura ----------
//
//  SIETE estados, y cada uno se pinta distinto (§5.4 del diseño):
//   · `leyendo`   todavía no contestó (D103): no se muestra NADA de antes;
//   · `ok`        leído, con filas;
//   · `vacio`     leído, sin ventas en el período — un vacío LEGÍTIMO;
//   · `prohibido` la base dijo 42501: no lo podés ver. NO es una lista vacía;
//   · `rechazo`   la base dijo que no por otra cosa (22023: el desde después
//                 del hasta, las monedas distintas). No se reintenta: se lee;
//   · `error`     no se pudo leer, o no contestó (D92);
//   · `incompleto` falta el desde o el hasta: no se pregunta.
//  Y un octavo que no es un estado sino su ausencia: la RESPUESTA VIEJA que
//  llega después de la nueva, que se descarta por el contador (`SEQ`).
type Estado = 'inicial' | 'leyendo' | 'ok' | 'vacio' | 'prohibido' | 'rechazo' | 'error' | 'incompleto';
let ESTADO: Estado = 'inicial', E_RN = '';
let FILAS: Fila[] = [];
let SEQ = 0;
//  Lo que se preguntó en la lectura EN PANTALLA. El detalle se pide con esto y
//  no con los controles de ahora (la fila tocada es de la consulta anterior).
let CONSULTA: (Periodo & { agrupar: string }) | null = null;

const plata = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
const cant = (n: number | null | undefined) => Number(n ?? 0).toLocaleString('es-AR');
//  El rótulo de la moneda, o NADA cuando no se pudo leer cuál es.
const enMoneda = () => MONEDA ? ` <span class="hint">${esc(MONEDA)}</span>` : '';

export async function cargarRentabilidad(){
  const mio = ++SEQ;
  MIRANDO = null; ABIERTA = null;
  limpiar('#rnMsg');
  //  La primera vez, lo que esta computadora recuerda (D206).
  if (!PREF_LEIDA){ PREF_LEIDA = true; leerPref(); marcarBotones(); }
  GASTOS = { estado: 'nada' };
  //  Lo que no depende de ninguna lectura va ANTES del `await`.
  $('#rnRangoForm').classList.toggle('oculto', $('#rnPeriodo').value !== 'rango');
  const agrupar = $('#rnAgrupar').value;
  $('#rnThGrupo').textContent = AGRUPA[agrupar]?.nombre ?? agrupar;
  ESTADO = 'leyendo'; FILAS = []; CONSULTA = null;
  pintar();

  await cfg();
  if (mio !== SEQ) return;
  const p = periodo();
  pintarCfg();
  if (!p){ ESTADO = 'incompleto'; pintar(); return; }
  CONSULTA = { ...p, agrupar };

  const { data, error } = await rpcLee('rentabilidad',
    { p_desde: p.desde, p_hasta: p.hasta, p_agrupar: agrupar });
  //  La respuesta vieja: mientras ésta viajaba se pidió otra.
  if (mio !== SEQ) return;
  if (error){
    E_RN = error.message;
    ESTADO = error.code === '42501' ? 'prohibido'
           : !noContesto(error) && error.code === '22023' ? 'rechazo'
           : 'error';
  } else {
    FILAS = (data as unknown as Fila[]) || [];
    ESTADO = FILAS.length ? 'ok' : 'vacio';
  }
  pintar();
  if (VISTA === 'todo' && ESTADO === 'ok') cargarGastos();
}

// ---------- los envíos y los gastos (D206) ----------
//
//  Se piden SÓLO con la tercera forma elegida, y para el período que está en
//  pantalla: las otras dos no los usan y no se le suma una consulta a cada
//  vistazo. Como la tabla, la respuesta vieja se descarta (`mio`), y un
//  error no se pinta como cero: el resultado queda sin número y se dice por
//  qué.
type Gastos = { envios: number; viajes: number; viajes_sin_cotizacion: number;
                gastos: number; gastos_n: number; gastos_sin_cotizacion: number };
let GASTOS: { estado: 'nada' | 'leyendo' | 'ok' | 'error'; g?: Gastos; e?: string } = { estado: 'nada' };
let SEQ_GASTOS = 0;
async function cargarGastos(){
  if (!CONSULTA) return;
  const mio = ++SEQ_GASTOS, seq = SEQ;
  GASTOS = { estado: 'leyendo' };
  pintarResumen();
  const { data, error } = await rpcLee('rentabilidad_gastos',
    { p_desde: CONSULTA.desde, p_hasta: CONSULTA.hasta });
  if (mio !== SEQ_GASTOS || seq !== SEQ) return;
  const g = ((data as unknown as Gastos[]) || [])[0];
  GASTOS = error || !g ? { estado: 'error', e: error?.message ?? 'no contestó nada' }
                       : { estado: 'ok', g };
  pintarResumen();
}

//  Lo que la pantalla no pudo leer de la configuración, dicho una vez arriba.
function pintarCfg(){
  const partes: string[] = [];
  if (E_CFG)
    partes.push(`No se pudo leer la configuración de la empresa (${esc(E_CFG)}): los montos
      van sin moneda` + ($('#rnPeriodo').value === 'rango' ? '.' : `, y el período se calculó
      con el reloj de esta computadora, que puede no ser el de la empresa.`));
  else if (ZONA_USADA === 'local' && $('#rnPeriodo').value !== 'rango')
    partes.push(`La zona horaria de la empresa (<span class="mono">${esc(ZONA)}</span>) no la
      conoce este navegador: las fechas del período se calcularon con el reloj de esta
      computadora.`);
  $('#rnCfgMsg').innerHTML = partes.map(t => `<div class="aviso a-warn">${t}</div>`).join('');
}

const totales = () => {
  const suma = (k: keyof Fila) => FILAS.reduce((s, f) => s + Number(f[k] ?? 0), 0);
  const conCosto = FILAS.filter(f => f.margen != null);
  const ventasConCosto = suma('ventas_con_costo');
  //  Margen y costo del TOTAL sólo existen si algún grupo los tiene; con todos
  //  en null sumar daría un 0,00 que nadie midió (D112).
  const costo    = conCosto.length ? conCosto.reduce((s, f) => s + Number(f.costo ?? 0), 0) : null;
  const comision = conCosto.length ? conCosto.reduce((s, f) => s + Number(f.comision ?? 0), 0) : null;
  //  La ganancia según la forma elegida (D206).
  const margen   = conCosto.length
    ? conCosto.reduce((s, f) => s + Number(ganancia(f.margen, f.comision)), 0) : null;
  return {
    unidades: suma('unidades'), ventas: suma('ventas'), ventasConCosto,
    costo, comision, margen,
    margenPct: pctDe(margen, ventasConCosto),
    sinCosto: suma('sin_costo'),
  };
};
//  ¿Llegó la respuesta entera? Con mil filas o más puede ser el corte del
//  servidor, y entonces ningún total se puede afirmar.
const cortada = () => FILAS.length >= TOPE_SERVIDOR;

function pintar(){
  const nombre = AGRUPA[CONSULTA?.agrupar ?? $('#rnAgrupar').value]?.nombre ?? '';
  const tbl = $('#tblRentabilidad');
  const vacio = (t: string) => `<tr><td colspan="8" class="vacio">${t}</td></tr>`;
  //  El resumen y el cartel se borran en TODOS los caminos que no son `ok`
  //  (no mostrar datos viejos como si fueran los de ahora).
  limpiar('#rnResumen'); limpiar('#rnSinCosto');
  $('#rnGraf').classList.add('oculto');
  $('#rnRango').innerHTML = CONSULTA
    ? `Del <b>${dma(CONSULTA.desde)}</b> al <b>${dma(CONSULTA.hasta)}</b>, por ${esc(nombre.toLowerCase())}.`
      + (MONEDA ? ` Montos en <b>${esc(MONEDA)}</b>.` : '')
    : '';

  if (ESTADO === 'inicial' || ESTADO === 'leyendo'){ tbl.innerHTML = vacio('Calculando…'); return; }
  if (ESTADO === 'incompleto'){ tbl.innerHTML = vacio('Elegí desde y hasta.'); return; }
  //  Ojo: el texto de la base NO se agrega entre paréntesis; duplicado, una
  //  aserción sobre la frase pasaría también en el estado de error.
  if (ESTADO === 'prohibido'){
    tbl.innerHTML = vacio('No tenés permiso para ver costos ni márgenes. '
      + 'Si te lo acaban de sacar —por ejemplo, se apagó en Ajustes la casilla de los '
      + 'administradores—, esta pestaña se va la próxima vez que entres.');
    return;
  }
  if (ESTADO === 'rechazo'){ tbl.innerHTML = vacio(`La base no lo calcula: ${esc(E_RN)}`); return; }
  if (ESTADO === 'error'){
    tbl.innerHTML = vacio(`No se pudo calcular la rentabilidad (${esc(E_RN)}). `
      + 'No quiere decir que no haya ventas: no se pudo preguntar.');
    return;
  }
  //  «Venta» es lo que la base cuenta como venta (configuración, D16): el
  //  texto no nombra el estado.
  if (ESTADO === 'vacio'){
    tbl.innerHTML = vacio('No hay ventas en este período.');
    return;
  }

  const t = totales();
  pintarResumen();

  //  EL CARTEL QUE NO SE APAGA (§5.4, D106). Cuando el número no puede decir
  //  todo, dice qué le falta: cuántos ítems quedaron afuera y sobre cuánta
  //  plata se calculó lo que sí se calculó.
  //
  //  DICE ÍTEMS, NO VENTAS NI UNIDADES: `sin_costo` es `count(*)` de LÍNEAS de
  //  pedido (cinco cargadores en una línea son un ítem y cinco unidades).
  //
  //  LA COMISIÓN TAMBIÉN ES PARCIAL: la base la suma sólo de los ítems con costo
  //  (es la que se resta del margen), y se dice.
  //
  //  EL CAMINO QUE ACONSEJA ES EL QUE ANDA: cargar el costo de una recepción
  //  recalcula lo de ESA recepción; lo vendido sin recepción anterior (stock
  //  inicial, un ajuste) no lo toma nunca.
  if (t.sinCosto > 0 && !cortada()){
    const donde = puede('costo','cargar') && puede('recepcion','ver')
      ? ' El costo que falta se carga en <b>Recepción → Costos de lo recibido</b>: las ventas que salieron de esa recepción se recalculan (tocá «Volver a calcular» para verlo); lo vendido sin ninguna recepción anterior —stock inicial, un ajuste— no toma ese costo.'
      : ' El costo que falta lo carga quien tiene permiso para cargar costos, en Recepción.';
    $('#rnSinCosto').innerHTML = `<div class="aviso a-warn">` + (t.margen == null
      ? (Number(t.sinCosto) === 1
         ? '<b>El único ítem vendido en este período no tiene costo cargado</b>: no hay margen que calcular.'
         : `<b>Ninguno de los ${cant(t.sinCosto)} ítems vendidos en este período tiene costo cargado</b>: no hay margen que calcular.`)
      : `<b>${Number(t.sinCosto) === 1 ? '1 ítem vendido en este período no tiene'
           : `${cant(t.sinCosto)} ítems vendidos en este período no tienen`} costo cargado</b> (un ítem es una línea de pedido, con todas sus unidades). El costo, la comisión, el margen y el % se
         calculan sólo sobre los que sí lo tienen: ${plata(t.ventasConCosto)} de ${plata(t.ventas)}${enMoneda()} vendidos.`)
      + donde + '</div>';
  }

  tbl.innerHTML = FILAS.map((f, i) => {
    //  Un grupo con PARTE de sus ítems sin costo tiene margen, pero de una
    //  parte: se dice en la fila, no sólo en el cartel de arriba.
    const parcial = f.margen != null && Number(f.sin_costo) > 0;
    const marca = parcial ? ' <span class="hint">· parcial</span>' : '';
    const etq = f.clave == null
      ? `<span class="hint" title="${esc(SIN_DATO[CONSULTA!.agrupar] || 'Sin dato')}">${esc(f.etiqueta)}</span>`
      : esc(f.etiqueta);
    return `<tr data-i="${i}" style="cursor:pointer">
      <td>${etq}</td>
      <td class="num">${cant(f.unidades)}</td>
      <td class="num">${plata(f.ventas)}</td>
      <td class="num">${f.costo == null ? sinCostoChip : plata(f.costo)}</td>
      <td class="num"${parcial ? ' title="Sólo la de los ítems que tienen costo"' : ''}>${
        f.comision == null ? '—' : plata(f.comision)}${marca}</td>
      <td class="num"${f.margen == null ? ' title="Ningún ítem de este grupo tiene costo"'
            : parcial ? ' title="Calculado sólo sobre los ítems que tienen costo"' : ''}>${
        f.margen == null ? '—' : `<b>${plata(ganancia(f.margen, f.comision))}</b>`}${marca}</td>
      <td class="num">${pct(pctFila(f))}</td>
      <td class="num">${Number(f.sin_costo) > 0
        ? `<span class="chip c-warn">${cant(f.sin_costo)}</span>` : '0'}</td>
    </tr>`;
  }).join('');
  pintarGrafico();
}

//  Los números de arriba. Aparte de `pintar()` porque los envíos y los
//  gastos llegan después: repintarlos no tiene que cerrar la fila abierta.
function pintarResumen(){
  limpiar('#rnResumen');
  if (ESTADO !== 'ok') return;
  const t = totales();
  const kpi = (tit: string, valor: string, sub = '') =>
    `<div class="kpi"><div class="k-tit">${tit}</div><div class="k-num">${valor}</div>${
      sub ? `<div class="k-sub">${sub}</div>` : ''}</div>`;
  //  CON LA RESPUESTA CORTADA NO HAY TOTALES (daría un número que nadie midió).
  //  Se dice por qué y la tabla se muestra igual: cada fila sí es cierta.
  if (cortada()){
    $('#rnResumen').innerHTML = `<div class="aviso a-warn">Llegaron ${cant(FILAS.length)} grupos,
      que es lo más que el servidor devuelve de una vez: puede haber más, y por eso los
      totales no se muestran. Elegí un período más corto o agrupá por algo con menos
      grupos.</div>`;
  } else {
    $('#rnResumen').innerHTML =
        kpi('Ventas', plata(t.ventas) + enMoneda(), `${cant(t.unidades)} ${Number(t.unidades) === 1 ? 'unidad' : 'unidades'}`)
      + kpi('Costo', t.costo == null ? sinCostoChip : plata(t.costo) + enMoneda())
      + kpi('Margen', t.margen == null ? sinCostoChip : plata(t.margen) + enMoneda(),
            t.comision == null ? ''
            : VISTA === 'costo' ? `venta − costo, antes de ${plata(t.comision)} de comisiones de esos ítems`
            : `después de ${plata(t.comision)} de comisiones de esos ítems`)
      + kpi('Margen %', t.margenPct == null ? '—' : pct(t.margenPct),
            t.margenPct == null ? '' : 'sobre las ventas con costo')
      + (VISTA === 'todo' ? kpisGastos(t.margen, t.ventasConCosto, Number(t.sinCosto), kpi, sinCostoChip) : '');
  }

}

//  El % de una fila: el de la base en la forma de siempre (redondeado allá,
//  así no cambia ni un decimal), y deducido en la de venta − costo.
const pctFila = (f: Fila) => VISTA === 'costo'
  ? pctDe(ganancia(f.margen, f.comision), Number(f.ventas_con_costo)) : f.margen_pct;

//  Los dos números de la tercera forma: cuánto se fue en envíos y gastos, y
//  lo que queda. Mientras se leen, o si no se pudieron leer, el resultado no
//  tiene número (restar cero sería afirmar que no hubo gastos).
function kpisGastos(margen: number | null, ventasConCosto: number, sinCosto: number,
                    kpi: (t: string, v: string, s?: string) => string, sinCostoChip: string){
  if (GASTOS.estado !== 'ok'){
    return kpi('Envíos y gastos', GASTOS.estado === 'error' ? '—' : '…',
               GASTOS.estado === 'error'
                 ? `No se pudieron leer (${esc(GASTOS.e ?? '')}): no se restó nada.`
                 : 'Leyendo…');
  }
  const g = GASTOS.g!;
  const fuera = Number(g.envios) + Number(g.gastos);
  const resto = margen == null ? null : margen - fuera;
  const sinCot = Number(g.viajes_sin_cotizacion) + Number(g.gastos_sin_cotizacion);
  return kpi('Envíos y gastos', plata(fuera) + enMoneda(),
             `${plata(g.envios)} de ${cant(g.viajes)} ${Number(g.viajes) === 1 ? 'envío' : 'envíos'} que paga la empresa`
             + ` y ${plata(g.gastos)} de ${cant(g.gastos_n)} ${Number(g.gastos_n) === 1 ? 'gasto' : 'gastos'} de caja`
             + (sinCot > 0 ? ` · <b>${cant(sinCot)} sin cotización del día: no se restaron</b>` : ''))
       + kpi('Ganancia final', resto == null ? sinCostoChip : plata(resto) + enMoneda(),
             resto == null ? ''
             //  PARCIAL (D112): con ítems sin costo, el margen es sólo de una
             //  parte y los envíos y gastos son de todo el período; con algo
             //  sin cotización, no se restó todo. Se dice, y sin %.
             : sinCosto > 0 || sinCot > 0
             ? '<b>Parcial:</b> ' + [sinCosto > 0 ? 'el margen es sólo de los ítems con costo y los envíos y gastos son de todo el período' : '',
                                     sinCot > 0 ? 'no se restó lo que no tiene cotización' : ''].filter(Boolean).join('; ') + '.'
             : `${pct(pctDe(resto, ventasConCosto))} sobre las ventas`);
}

// ---------- el gráfico (D206) ----------
//
//  Barras horizontales, una por fila, dibujadas a mano en SVG (sin
//  bibliotecas: la página no carga ninguna). Muestra lo que se elige arriba
//  (ganancia en la forma elegida, ventas, margen % o unidades) y cambia con
//  cada cosa que se toca: el período, el agrupador, la forma o la métrica.
//   · Las fechas van en orden; lo demás, de mayor a menor, con las 15
//     primeras y el resto sumado en «Otros» (el % no se suma: el resto se
//     dice en el pie).
//   · Un grupo sin costo NO es una barra de cero (D112): dice «sin costo».
//   · Tocar una barra abre esa fila en la tabla, como tocar la fila.
const TOPE_BARRAS = 15;
function pintarGrafico(){
  const caja = $('#rnGraf');
  if (ESTADO !== 'ok' || !FILAS.length){ caja.classList.add('oculto'); return; }
  caja.classList.remove('oculto');
  const valor = (f: Fila): number | null =>
    METRICA === 'ventas' ? Number(f.ventas)
    : METRICA === 'unidades' ? Number(f.unidades)
    : METRICA === 'pct' ? pctFila(f)
    : ganancia(f.margen, f.comision);
  const porFecha = ['dia','semana','mes'].includes(CONSULTA?.agrupar ?? '');
  type Barra = { i: number | null; etq: string; v: number | null };
  let barras: Barra[] = FILAS.map((f, i) => ({ i, etq: f.etiqueta, v: valor(f) }));
  if (!porFecha)
    barras.sort((a, b) => (b.v ?? -Infinity) - (a.v ?? -Infinity));
  let pie = '';
  if (barras.length > TOPE_BARRAS){
    const resto = barras.slice(TOPE_BARRAS);
    barras = barras.slice(0, TOPE_BARRAS);
    const conV = resto.filter(b => b.v != null);
    const faltan = resto.length - conV.length;
    if (METRICA !== 'pct' && conV.length)
      barras.push({ i: null, etq: `Otros (${cant(resto.length)}${faltan ? ', parcial' : ''})`,
                    v: conV.reduce((s, b) => s + Number(b.v), 0) });
    pie = `Se dibujan los ${TOPE_BARRAS} primeros de ${cant(FILAS.length)}`
      + (METRICA !== 'pct' && conV.length
         ? (faltan ? `; el resto va sumado en «Otros», menos ${cant(faltan)} sin costo.` : '; el resto va sumado en «Otros».')
         : '; el resto está en la tabla.');
  }
  if (porFecha && FILAS.length > TOPE_BARRAS) pie = pie.replace('primeros', 'primeros (por fecha)');
  const nums = barras.filter(b => b.v != null).map(b => Number(b.v));
  const max = Math.max(0, ...nums), min = Math.min(0, ...nums);
  //  Al ancho que de verdad tiene (en el celular, el del teléfono): así la
  //  letra queda del tamaño de la página y no achicada con el dibujo.
  const W = Math.max(300, Math.round($('#rnGrafDibujo').clientWidth || 760));
  const ETQ = Math.round(Math.min(230, W * 0.36)), NUM = W < 500 ? 78 : 110, ALTO = 28;
  const ancho = W - ETQ - NUM;
  const rango = (max - min) || 1;
  const x = (v: number) => ETQ + (v - min) / rango * ancho;
  const cero = x(0);
  const fmt = (v: number) => METRICA === 'pct' ? pct(v) : METRICA === 'unidades' ? cant(v) : plata(v);
  const largo = Math.max(10, Math.floor(ETQ / 7.5));
  const corto = (t: string) => t.length > largo ? t.slice(0, largo - 1) + '…' : t;
  const alto = barras.length * ALTO + 6;
  const filasSvg = barras.map((b, k) => {
    const y = k * ALTO + 4;
    const etq = `<text class="g-txt" x="${ETQ - 8}" y="${y + 17}" text-anchor="end"><title>${esc(b.etq)}</title>${esc(corto(b.etq))}</text>`;
    if (b.v == null)
      return etq + `<text class="g-sin" x="${cero + 6}" y="${y + 17}">sin costo</text>`;
    const v = Number(b.v), x0 = Math.min(cero, x(v)), w = Math.max(1, Math.abs(x(v) - cero));
    const clic = b.i == null ? '' : ` data-i="${b.i}" role="button" tabindex="0" aria-label="${esc(b.etq)}: ${fmt(v)}. Abrir la fila"`;
    return etq
      + `<rect class="g-barra${v < 0 ? ' neg' : ''}${b.i != null && ABIERTA === `${SEQ}:${b.i}` ? ' sel' : ''}"${clic}`
      + ` x="${x0}" y="${y + 3}" width="${w}" height="${ALTO - 8}" rx="3"><title>${esc(b.etq)}: ${fmt(v)}</title></rect>`
      + `<text class="g-num" x="${Math.max(x(v), cero) + 6}" y="${y + 17}">${fmt(v)}</text>`;
  }).join('');
  $('#rnGrafDibujo').innerHTML = `<svg viewBox="0 0 ${W} ${alto}" role="group"
      aria-label="${esc(({ margen: 'Ganancia', ventas: 'Ventas', pct: 'Margen %', unidades: 'Unidades' } as any)[METRICA])} por ${esc((AGRUPA[CONSULTA!.agrupar]?.nombre ?? '').toLowerCase())}">
      <line class="g-eje" x1="${cero}" x2="${cero}" y1="0" y2="${alto}"/>${filasSvg}</svg>`;
  const sinCosto = barras.some(b => b.v == null);
  $('#rnGrafPie').textContent = [pie,
    sinCosto ? '«Sin costo»: ningún ítem de ese grupo tiene costo cargado, así que no hay ganancia que dibujar.' : '',
    METRICA === 'margen' && VISTA === 'todo' ? 'Los envíos y los gastos no son de ningún grupo: se restan sólo en el total de arriba.' : '',
  ].filter(Boolean).join(' ');
}

// ---------- el detalle de una fila ----------
//
//  Como el historial de Equipos: se guarda QUÉ se mira, se pinta «leyendo…», y
//  al llegar la respuesta se busca la fila en el DOM de AHORA y se descarta si
//  ya no es lo que se pide.
let MIRANDO: string | null = null;

const panel = (i: number, dentro: string) => {
  const fila = $(`#tblRentabilidad tr[data-i="${i}"]`);
  if (!fila) return;
  document.getElementById('filaRnDetalle')?.remove();
  const tr = document.createElement('tr');
  tr.id = 'filaRnDetalle';
  tr.innerHTML = `<td colspan="8"><div class="confirmar">
      <div class="conf-tit">${esc(FILAS[i]?.etiqueta ?? '')}</div>${dentro}</div></td>`;
  fila.after(tr);
};

//  Los argumentos del detalle para la fila `i`, sacados de la CONSULTA que
//  está en pantalla. Tres casos:
//   · agrupado por una fila de otra tabla: el filtro por su id;
//   · agrupado por fecha: el desde/hasta de ESE día, semana o mes, recortado
//     al período (la semana de un lunes anterior al «desde» no se extiende);
//   · la fila «(sin dato)»: un filtro en null quiere decir «todos», así que
//     el «no tiene» va en `p_sin_dato` (0054) y filtra la base antes del tope.
//     Sólo categoría y proveedor pueden faltar; en otro agrupador la base
//     rechaza (22023) y el panel lo dice.
const argsDetalle = (f: Fila) => {
  const c = CONSULTA!;
  const a: Record<string, unknown> = { p_desde: c.desde, p_hasta: c.hasta };
  const g = AGRUPA[c.agrupar];
  if (c.agrupar === 'dia' && f.clave){ a.p_desde = f.clave; a.p_hasta = f.clave; }
  else if (c.agrupar === 'semana' && f.clave){
    a.p_desde = mayor(f.clave, c.desde); a.p_hasta = menor(sumarDias(f.clave, 6), c.hasta);
  } else if (c.agrupar === 'mes' && f.clave){
    a.p_desde = mayor(f.clave + '-01', c.desde); a.p_hasta = menor(finDeMes(f.clave + '-01'), c.hasta);
  } else if (g?.filtro){
    if (f.clave != null) a[g.filtro] = Number(f.clave);
    else a.p_sin_dato = c.agrupar;
  }
  return a;
};

//  DOS cosas distintas: QUÉ FILA está abierta (un segundo clic la cierra) y QUÉ
//  PEDIDO es el vigente (descarta la respuesta vieja). Ojo: no unirlas en una
//  clave «lectura:fila»; reabrir la MISMA fila dejaría que la respuesta lenta
//  del primer clic pise a la del tercero.
let ABIERTA: string | null = null, PEDIDO_DET = 0;

$('#tblRentabilidad').onclick = e => {
  //  Un clic ADENTRO del panel no lo cierra (es una fila de la misma tabla).
  if (el(e.target).closest('#filaRnDetalle')) return;
  const fila = el(e.target).closest('tr[data-i]');
  abrirFila(fila ? Number(fila.dataset.i) : null);
};
//  Tocar una barra del gráfico es tocar su fila (D206): se abre y se lleva
//  a la vista.
const desdeBarra = (e: Event) => {
  const b = el(e.target).closest('[data-i]');
  if (!b) return;
  const i = Number(b.dataset.i);
  abrirFila(i);
  $(`#tblRentabilidad tr[data-i="${i}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
};
$('#rnGrafDibujo').onclick = desdeBarra;
//  Si cambia el ancho (girar el teléfono, agrandar la ventana), se vuelve a
//  dibujar al ancho nuevo.
let RESIZE: number | undefined;
window.addEventListener('resize', () => {
  clearTimeout(RESIZE);
  RESIZE = window.setTimeout(() => {
    if (!$('#rnGraf').classList.contains('oculto')) pintarGrafico();
  }, 150);
});
$('#rnGrafDibujo').onkeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' '){ e.preventDefault(); desdeBarra(e); }
};

async function abrirFila(i: number | null){
  document.getElementById('filaRnDetalle')?.remove();
  const mio = ++PEDIDO_DET;
  MIRANDO = null;
  if (i == null || !CONSULTA || !FILAS[i]){ ABIERTA = null; marcarBarra(); return; }
  const cual = `${SEQ}:${i}`;
  if (ABIERTA === cual){ ABIERTA = null; marcarBarra(); return; }
  ABIERTA = cual;
  marcarBarra();
  MIRANDO = String(mio);
  const f = FILAS[i];
  panel(i, '<p class="conf-txt">Leyendo los ítems…</p>');

  //  Los agrupadores de la 0090 se abren por su clave; los de siempre, con
  //  sus filtros (`rentabilidad_detalle`).
  const { data, error } = AGRUPA[CONSULTA.agrupar]?.porClave
    ? await rpcLee('rentabilidad_grupo', { p_desde: CONSULTA.desde, p_hasta: CONSULTA.hasta,
                                            p_agrupar: CONSULTA.agrupar, p_clave: f.clave } as any)
    : await rpcLee('rentabilidad_detalle', argsDetalle(f) as any);
  if (MIRANDO !== String(mio)) return;
  if (error){
    //  Tres respuestas, como la tabla de arriba: no lo podés ver, la base
    //  dijo que no (con su motivo), o no se pudo preguntar.
    panel(i, `<p class="conf-txt">${error.code === '42501'
      ? 'No tenés permiso para ver costos ni márgenes.'
      : !noContesto(error) && error.code === '22023'
      ? `La base no lo calcula: ${esc(error.message)}`
      : `No se pudieron leer los ítems (${esc(error.message)}). No quiere decir que no haya: no se pudo preguntar.`}</p>`);
    return;
  }
  const items = (data as unknown as Item[]) || [];
  //  Cortada por la base (`tope`, más de 2.000) o por el servidor (mil filas
  //  o más sin que la base lo diga): en los dos casos puede haber más.
  const corte = items.some(x => x.tope) || items.length >= TOPE_SERVIDOR;
  const notas: string[] = [];
  if (corte)
    notas.push(`Se muestran los ${cant(items.length)} ítems más recientes, que es lo más que llega de una vez: puede haber más.`);
  if (!items.length){
    panel(i, `<p class="conf-txt">${corte ? notas[0]
      : 'No hay ítems: puede haber cambiado algo desde que se calculó. Tocá «Volver a calcular».'}</p>`);
    return;
  }
  panel(i, `${notas.map(t => `<p class="conf-txt">${t}</p>`).join('')}
    <div class="tw" style="max-height:20rem"><table>
      <thead><tr><th>Día</th><th>Pedido</th><th>Cliente</th><th>Vendedor</th><th>Producto</th>
        <th>Serie</th><th class="num">Cant.</th><th class="num">Neto</th><th class="num">Costo</th>
        <th class="num">Comisión</th><th class="num">Margen</th></tr></thead>
      <tbody>${items.map(x => `<tr>
        <td class="hint">${esc(dma(String(x.dia)))}</td>
        <td class="mono">${esc(x.pedido)}</td>
        <td>${esc(x.cliente ?? '—')}</td>
        <td>${esc(x.vendedor ?? '—')}</td>
        <td><span class="mono">${esc(x.producto)}</span> ${esc(x.descripcion)}</td>
        <td class="mono">${esc(x.serie ?? '—')}</td>
        <td class="num">${cant(x.cantidad)}</td>
        <td class="num">${plata(x.neto)}</td>
        <td class="num">${x.costo == null ? '<span class="chip c-warn">sin costo</span>' : plata(x.costo)}</td>
        <td class="num">${plata(x.comision)}</td>
        <td class="num">${x.margen == null ? '—' : `<b>${plata(ganancia(x.margen, x.comision))}</b>`}</td>
      </tr>`).join('')}</tbody></table></div>`);
}
//  La barra de la fila abierta, resaltada.
const marcarBarra = () => document.querySelectorAll('#rnGrafDibujo [data-i]').forEach(b =>
  b.classList.toggle('sel', ABIERTA === `${SEQ}:${(b as HTMLElement).dataset.i}`));

//  Los controles: cualquier cambio vuelve a preguntar.
$('#rnPeriodo').onchange = () => {
  //  Al pasar a «elegir», los dos campos arrancan con el período que se
  //  estaba mirando, en vez de vacíos.
  if ($('#rnPeriodo').value === 'rango' && CONSULTA && !$('#rnDesde').value && !$('#rnHasta').value){
    $('#rnDesde').value = CONSULTA.desde; $('#rnHasta').value = CONSULTA.hasta;
  }
  cargarRentabilidad();
};
$('#rnAgrupar').onchange = () => { guardarPref(); cargarRentabilidad(); };
//  La forma de la ganancia y lo que muestra el gráfico NO vuelven a
//  preguntar: se repinta lo que ya llegó (la tercera forma, además, pide los
//  envíos y los gastos del período, una vez). La fila abierta se cierra:
//  sus números eran de la otra forma.
$('#rnVista').onclick = e => {
  const v = el(e.target).closest('button')?.dataset.v as Vista | undefined;
  if (!v || v === VISTA) return;
  VISTA = v; guardarPref(); marcarBotones();
  document.getElementById('filaRnDetalle')?.remove();
  ABIERTA = null; MIRANDO = null; ++PEDIDO_DET;
  pintar();
  //  Uno que falló se vuelve a pedir.
  if (VISTA === 'todo' && ESTADO === 'ok' && (GASTOS.estado === 'nada' || GASTOS.estado === 'error')) cargarGastos();
};
$('#rnMetrica').onclick = e => {
  const m = el(e.target).closest('button')?.dataset.m as Metrica | undefined;
  if (!m || m === METRICA) return;
  METRICA = m; guardarPref(); marcarBotones();
  pintarGrafico();
};
$('#rnDesde').onchange   = () => cargarRentabilidad();
$('#rnHasta').onchange   = () => cargarRentabilidad();
$('#rnRecalcular').onclick = () => cargarRentabilidad();
